import { useToastEscape } from "@/components/toast/use-toast-escape";
import type { FocusTrap } from "focus-trap";
import { OverlayBackdrop } from "@/components/modal/OverlayBackdrop";
import { OverlayContext } from "@/components/modal/overlay-context";
import { OverlayManager } from "@/components/modal/overlay-manager";
import { ZIndex } from "@/constants/z-index";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

export const OverlayProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [focusTrapStack] = useState<FocusTrap[]>([]);
  const [manager] = useState(() => new OverlayManager());
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [toastRoot, setToastRoot] = useState<HTMLElement | null>(null);
  const dismissToastsRef = useToastEscape();
  const lastContentFocus = useRef<HTMLElement | null>(null);
  const entries = useSyncExternalStore(manager.subscribe, manager.getSnapshot, manager.getSnapshot);

  useEffect(() => {
    const host = document.createElement("div");
    host.dataset.overlayRoot = "";
    Object.assign(host.style, {
      position: "relative",
      zIndex: String(ZIndex.OVERLAY_ROOT),
      isolation: "isolate",
    });
    document.body.appendChild(host);
    const notifications = document.createElement("div");
    notifications.dataset.globalToastRoot = "";
    notifications.className = "design-system-typography";
    Object.assign(notifications.style, {
      position: "relative",
      zIndex: String(ZIndex.GLOBAL_TOAST),
      isolation: "isolate",
      pointerEvents: "none",
    });
    document.body.appendChild(notifications);
    setRoot(host);
    setToastRoot(notifications);
    const rememberContentFocus = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && !notifications.contains(event.target)) {
        lastContentFocus.current = event.target;
      }
    };
    document.addEventListener("focusin", rememberContentFocus);
    window.addEventListener("keydown", manager.captureEscapeOwner, true);
    return () => {
      window.removeEventListener("keydown", manager.captureEscapeOwner, true);
      host.remove();
      notifications.remove();
      document.removeEventListener("focusin", rememberContentFocus);
    };
  }, [manager]);

  useLayoutEffect(() => {
    if (!root) return;
    const highestBlockingIndex = entries.findLastIndex((entry) => entry.blocking);
    if (highestBlockingIndex < 0) return;
    const originalStates = new Map<HTMLElement, { inert: boolean; ariaHidden: string | null }>();
    const makeInert = (element: HTMLElement) => {
      if (originalStates.has(element)) return;
      originalStates.set(element, {
        inert: element.inert,
        ariaHidden: element.getAttribute("aria-hidden"),
      });
      element.inert = true;
      element.setAttribute("aria-hidden", "true");
    };
    const disableBackgroundInteraction = () => {
      for (const element of document.body.children) {
        if (element instanceof HTMLElement && element !== root && element !== toastRoot)
          makeInert(element);
      }
      entries.slice(0, highestBlockingIndex).forEach((entry) => makeInert(entry.element));
    };
    disableBackgroundInteraction();
    const observer = new MutationObserver(disableBackgroundInteraction);
    observer.observe(document.body, { childList: true });
    return () => {
      observer.disconnect();
      originalStates.forEach((original, element) => {
        element.inert = original.inert;
        if (original.ariaHidden == null) element.removeAttribute("aria-hidden");
        else element.setAttribute("aria-hidden", original.ariaHidden);
      });
    };
  }, [entries, root, toastRoot]);

  const restoreToastFocus = useCallback(() => {
    const currentEntries = manager.getSnapshot();
    const blockingIndex = currentEntries.findLastIndex((entry) => entry.blocking);
    const previous = lastContentFocus.current;
    const allowed =
      blockingIndex < 0 ||
      currentEntries
        .slice(blockingIndex)
        .some((entry) => previous && entry.element.contains(previous));
    if (previous?.isConnected && allowed && !previous.closest('[inert], [aria-hidden="true"]')) {
      previous.focus({ preventScroll: true });
      if (document.activeElement === previous) return;
    }
    const modal = currentEntries[blockingIndex]?.element;
    (modal?.querySelector<HTMLElement>('[role="dialog"][tabindex]') ?? modal)?.focus({
      preventScroll: true,
    });
  }, [manager]);

  const value = useMemo(
    () => ({ manager, root, toastRoot, restoreToastFocus, focusTrapStack, dismissToastsRef }),
    [manager, root, toastRoot, restoreToastFocus, focusTrapStack, dismissToastsRef],
  );
  return (
    <OverlayContext.Provider value={value}>
      {children}
      <OverlayBackdrop entries={entries} root={root} />
    </OverlayContext.Provider>
  );
};
